import React, { useRef, useState, useEffect } from 'react';

import type { ButtonProps } from '@mui/material';
import { Popover } from '@mui/material';

import Icon from 'src/components/ui/Icon';
import Button from 'src/components/ui/Button';
import Badge from 'src/components/ui/Badge';
import SavedSearchesTutorialPopover from './SavedSearchesTutorialPopover';

import { useSearchAdsCountSinceLastViewedQuery } from 'src/hooks/__generated__/queries';

import { maxNotifications } from 'src/constants/misc';
import { colours } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';
import { useAdSearchState } from '../contexts/AdSearchStateContext';

const StyledPopover = styledComponent(Popover)`
    .MuiPaper-root {
        background-color: transparent;
        box-shadow: none;
    }
    .MuiBackdrop-invisible {
        background-color: ${colours.black.main};
        opacity: 0.5!important;
    }
`;

const StyledButton = styledComponent(Button)`
    $ > span {
        min-width: 0;
    }
`;

const StyledBadge = styledComponent(Badge)<{ $width: string }>`
    .MuiBadge-badge {
        height: 0.875rem;
        width: ${({ $width }) => $width};
        min-width: 0;
        font-size: 0.6rem;
        line-height: 0.875rem;
    }
`;

export type SavedSearchesIconButtonProps = ButtonProps & {
    openSavedSearchTutorial?: boolean;
};

const SavedSearchesIconButton = ({ openSavedSearchTutorial, ...rest }: SavedSearchesIconButtonProps) => {
    const [tutorialAnchorEl, setTutorialAnchorEl] = useState<HTMLButtonElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const tutorialPopoverId = openSavedSearchTutorial ? 'tutorial-popover' : undefined;
    const { changeAdSearchState } = useAdSearchState();

    useEffect(() => {
        if (openSavedSearchTutorial && buttonRef.current) {
            setTutorialAnchorEl(buttonRef.current);
        }
    }, [openSavedSearchTutorial]);

    const handleCloseTutorial = async () => {
        await changeAdSearchState({ openSavedSearchTutorial: false });
        setTutorialAnchorEl(null);
    };

    const { data } = useSearchAdsCountSinceLastViewedQuery({
        fetchPolicy: 'cache-and-network',
    });
    const totalAdCountSinceLastViewed = data?.searchAdsCountSinceLastViewed ?? 0;
    return (
        <>
            <StyledButton {...rest} ref={buttonRef} aria-describedby={tutorialPopoverId}>
                <StyledBadge
                    badgeContent={totalAdCountSinceLastViewed}
                    color="tertiary"
                    invisible={!totalAdCountSinceLastViewed}
                    max={maxNotifications}
                    $width={totalAdCountSinceLastViewed > maxNotifications ? '1.5rem' : '0.875rem'}
                >
                    <Icon name="SavedSearches" />
                </StyledBadge>
            </StyledButton>
            <StyledPopover
                id={tutorialPopoverId}
                open={openSavedSearchTutorial === true}
                anchorEl={tutorialAnchorEl}
                onClose={handleCloseTutorial}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <SavedSearchesTutorialPopover handleCloseTutorial={handleCloseTutorial} />
            </StyledPopover>
        </>
    );
};

export default SavedSearchesIconButton;
